import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { showSuccess, showError } from '../../components/CustomAlert';
import { FaBook, FaRegCalendarAlt, FaChevronDown, FaChevronRight, FaCalendarAlt, FaUpload, FaSearch, FaPlus, FaTrash, FaTimes, FaPencilAlt } from 'react-icons/fa';
import { toast } from 'react-hot-toast';
import { CourseSelect, SessionSelect } from '../../components/MasterDataSelect';
import { API_BASE_URL, getAuthHeaders } from '../../utils/apiConfig';
import { getStudentDetails } from '../../utils/studentsApi';
import { showDeleteConfirm } from '../../components/CustomConfirm';
import Modal from 'react-modal';

// Native <input type="time"> values are HH:mm (24h). Labels match user-facing times.
const DEFAULT_PRESENT_TIMES = { joined: '10:00', left: '14:30' };

const TIME_PRESETS = [
  { key: 'saturday', joined: '10:00', left: '16:30', label: 'Saturday 10:00 AM to 4:30 PM' },
  { key: 'clinicals', joined: '06:30', left: '14:30', label: 'Clinicals 6:30 AM to 2:30 PM' },
  { key: 'night', joined: '17:30', left: '21:30', label: 'Night Class 5:30 PM to 9:30 PM' },
];

function toTimeInputValue(value) {
  if (!value) return '';
  const match = String(value).trim().match(/^(\d{1,2}):(\d{2})/);
  if (!match) return '';
  return `${match[1].padStart(2, '0')}:${match[2]}`;
}

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const AddAttendanceContainer = styled.div`
  padding: 30px;
  background: #f8f9fa;
  min-height: 100vh;
  width: 100%;
  box-sizing: border-box;
  animation: ${fadeIn} 0.5s ease-out;
  
  @media (max-width: 768px) {
    padding: 20px 15px;
  }
  
  @media (max-width: 480px) {
    padding: 15px 10px;
  }
`;

const Title = styled.h1`
  color: #2c3e50;
  margin-bottom: 30px;
  font-size: 1.7rem;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 25px;
  }
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
    margin-bottom: 20px;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 30px;
`;

const TopRow = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: flex-end;
  margin-bottom: 30px;
  margin-top: 24px;
  width: 100%;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 25px;
    margin-top: 20px;
  }
  
  @media (max-width: 480px) {
    gap: 0.8rem;
    margin-bottom: 20px;
    margin-top: 15px;
  }
`;

const TopRowItem = styled.div`
  flex: 1 1 0;
  min-width: 0;
  
  @media (max-width: 768px) {
    flex: none;
    width: 100%;
  }
`;

const UniformInput = styled.input`
  height: 48px;
  padding: 12px 15px 12px 45px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  width: 100%;
  background: white;
  appearance: none;
  
  @media (max-width: 768px) {
    height: 48px;
    font-size: 16px; /* Prevent zoom on iOS */
  }
  
  @media (max-width: 480px) {
    padding: 12px 15px 12px 45px;
    font-size: 16px;
  }
`;

const Select = styled.select`
  padding: 12px 15px 12px 45px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  width: 100%;
  background: white;
  appearance: none;
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);
    box-shadow: 0 0 0 3px rgba(51, 66, 147, 0.1);
  }

  &:disabled {
    background-color: #f1f3f5;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    font-size: 16px; /* Prevent zoom on iOS */
  }
`;

const UniformSelect = styled(Select)`
  height: 48px;
  padding: 12px 15px 12px 45px;
  border-radius: 8px;
  font-size: 1rem;
  
  @media (max-width: 768px) {
    font-size: 16px; /* Prevent zoom on iOS */
  }
`;

const TimeInput = styled.input`
  height: 48px;
  padding: 12px 15px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  width: 100%;
  background: white;
  appearance: none;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);
    box-shadow: 0 0 0 3px rgba(51, 66, 147, 0.1);
  }
  
  @media (max-width: 768px) {
    height: 48px;
    font-size: 16px; /* Prevent zoom on iOS */
  }
`;

const LeaveReasonInput = styled.input`
  height: 48px;
  padding: 12px 15px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  width: 100%;
  background: white;
  appearance: none;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);
    box-shadow: 0 0 0 3px rgba(51, 66, 147, 0.1);
  }
  
  @media (max-width: 768px) {
    height: 48px;
    font-size: 16px; /* Prevent zoom on iOS */
  }
`;

const ChevronIcon = styled(FaChevronDown)`
  position: absolute;
  right: 15px;
  color: #adb5bd;
  pointer-events: none;
`;

const StudentListContainer = styled.div`
  margin-top: 1rem;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
  animation: ${fadeIn} 0.3s ease-out;
  
  @media (max-width: 768px) {
    margin-top: 0.8rem;
  }
`;

const StudentListHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
  align-items: center;
  padding: 1rem;
  background: #f8f9fa;
  font-weight: 600;
  color: #495057;
  
  @media (max-width: 768px) {
    display: none; /* Hide header on mobile */
  }
`;

const StudentItem = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #e9ecef;

  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 768px) {
    display: block;
    padding: 1.2rem;
    border-bottom: 1px solid #e9ecef;
    
    &:last-child {
      border-bottom: none;
    }
  }
`;

const StudentName = styled.span`
  font-weight: 500;
  color: #2c3e50;
  
  @media (max-width: 768px) {
    display: block;
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
  }
`;

const SubmitButton = styled.button`
  background: rgb(51, 66, 147);
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 1rem;
  align-self: flex-start;
  transition: all 0.2s ease;

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  &:not(:disabled):hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(51, 66, 147, 0.3);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 15px 30px;
    font-size: 1.1rem;
    margin-top: 1rem;
  }
`;

const InfoText = styled.p`
  text-align: center;
  color: #6c757d;
  padding: 2rem;
  background: #f1f3f5;
  border-radius: 8px;
  margin-top: 1rem;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    font-size: 0.95rem;
  }
  
  @media (max-width: 480px) {
    padding: 1.2rem;
    font-size: 0.9rem;
  }
`;

const LoadingText = styled(InfoText)``;

const RightAlignedButton = styled.button`
  float: right;
  margin-bottom: 20px;
  background: #3f51b5;
  color: white;
  border: none;
  padding: 10px 22px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.2s ease;
  &:hover {
    background: #283593;
  }
`;

const ModalContent = styled.div`
  background: white;
  padding: 30px;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (max-width: 768px) {
    padding: 25px 20px;
  }
  
  @media (max-width: 480px) {
    padding: 20px 15px;
  }
`;

const ModalButtonRow = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 10px;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 12px;
    width: 100%;
  }
`;

const ErrorModalContent = styled.div`
  background: white;
  padding: 30px;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  max-width: 600px;
  width: 90vw;
  max-height: 80vh;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 25px 20px;
    max-width: 95vw;
  }

  @media (max-width: 480px) {
    padding: 20px 15px;
  }
`;

const ErrorModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #fee;
`;

const ErrorIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #fee;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #d32f2f;
  font-size: 20px;
  font-weight: bold;
  flex-shrink: 0;
`;

const ErrorModalTitle = styled.h2`
  color: rgb(51, 66, 147);
  margin: 0;
  font-size: 1.5rem;
  flex: 1;

  @media (max-width: 480px) {
    font-size: 1.3rem;
  }
`;

const ErrorSummary = styled.div`
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
  color: #856404;
  font-weight: 500;
`;

const ErrorList = styled.div`
  flex: 1;
  overflow-y: auto;
  margin-bottom: 20px;
  max-height: 400px;
  padding-right: 10px;
  min-height: 100px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const ErrorItem = styled.div`
  padding: 14px;
  margin-bottom: 12px;
  background: #fff5f5;
  border-left: 4px solid #d32f2f;
  border-radius: 6px;
  color: #c53030;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.6;
  word-wrap: break-word;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const SuccessSummary = styled.div`
  background: #d4edda;
  border: 1px solid #28a745;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
  color: #155724;
  font-weight: 500;
`;

const SuccessList = styled.div`
  flex: 1;
  overflow-y: auto;
  margin-bottom: 20px;
  max-height: 400px;
  padding-right: 10px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const SuccessItem = styled.div`
  padding: 12px;
  margin-bottom: 10px;
  background: #d4edda;
  border-left: 4px solid #28a745;
  border-radius: 4px;
  color: #155724;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const SuccessIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #d4edda;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #28a745;
  font-size: 20px;
  font-weight: bold;
  flex-shrink: 0;
`;

const ErrorModalButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 15px;
  border-top: 1px solid #e9ecef;

  @media (max-width: 480px) {
    flex-direction: column;
    width: 100%;
  }
`;

const ErrorModalButton = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
  min-width: 120px;

  @media (max-width: 480px) {
    width: 100%;
    min-width: unset;
  }

  &.primary {
    background: #3f51b5;
    color: white;

    &:hover {
      background: #303f9f;
    }
  }

  &.secondary {
    background: #f5f5f5;
    color: #333;

    &:hover {
      background: #e0e0e0;
    }
  }
`;

const SelectGroup = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SelectIcon = styled.div`
  position: absolute;
  left: 15px;
  color: #adb5bd;
`;

const UniformButton = styled(RightAlignedButton)`
  height: 48px;
  min-width: 200px;
  margin-bottom: 0;
  &:disabled {
    background-color: #ccc;
    color: #888;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    min-width: auto;
    height: 48px;
    font-size: 1rem;
    padding: 12px 20px;
  }
`;

const FormContainer = styled.div`
  background: white;
  padding: 30px;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  
  @media (max-width: 768px) {
    padding: 25px 20px;
  }
  
  @media (max-width: 480px) {
    padding: 20px 15px;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  
  @media (max-width: 768px) {
    gap: 1.5rem;
  }
`;

// Mobile-specific styles for student information
const MobileStudentInfo = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    margin-bottom: 1rem;
    
    .student-detail {
      display: flex;
      justify-content: space-between;
      margin-bottom: 0.5rem;
      font-size: 0.9rem;
      color: #6c757d;
    }
    
    .student-detail-label {
      font-weight: 600;
      color: #495057;
    }
  }
`;

const MobileAttendanceSection = styled.div`
  .time-inputs {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    align-items: center;
    margin-bottom: 0.5rem;
  }

  .time-presets {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    margin-bottom: 0.5rem;
  }

  @media (max-width: 768px) {
    .attendance-select {
      width: 100%;
      margin-bottom: 1rem;
      font-size: 16px; /* Prevent zoom on iOS */
    }
    
    .time-inputs {
      gap: 1rem;
      margin-bottom: 1rem;
    }
    
    .time-input {
      flex: 1;
      height: 48px;
      padding: 12px 15px;
      border: 1px solid #e9ecef;
      border-radius: 8px;
      font-size: 16px; /* Prevent zoom on iOS */
      background: white;
      transition: all 0.2s ease;
      
      &:focus {
        outline: none;
        border-color: rgb(51, 66, 147);
        box-shadow: 0 0 0 3px rgba(51, 66, 147, 0.1);
      }
    }
    
    .leave-reason-input {
      width: 100%;
      height: 48px;
      padding: 12px 15px;
      border: 1px solid #e9ecef;
      border-radius: 8px;
      font-size: 16px; /* Prevent zoom on iOS */
      margin-bottom: 1rem;
      background: white;
      transition: all 0.2s ease;
      
      &:focus {
        outline: none;
        border-color: rgb(51, 66, 147);
        box-shadow: 0 0 0 3px rgba(51, 66, 147, 0.1);
      }
    }
  }
`;

// Student Search Styles
const SearchSection = styled.div`
  background: white;
  padding: 25px;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  margin-bottom: 30px;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 20px 15px;
  }
`;

const SearchTitle = styled.h3`
  color: #2c3e50;
  margin-bottom: 20px;
  font-size: 1.3rem;
  font-weight: 600;
`;

const SearchInput = styled.input`
  height: 48px;
  padding: 12px 45px 12px 45px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  width: 100%;
  background: white;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);
    box-shadow: 0 0 0 3px rgba(51, 66, 147, 0.1);
  }
  
  @media (max-width: 768px) {
    font-size: 16px; /* Prevent zoom on iOS */
  }
`;

const SearchInputContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #adb5bd;
  pointer-events: none;
`;

const ClearSearchIcon = styled.div`
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #adb5bd;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    color: #6c757d;
  }
`;

const SearchResultsContainer = styled.div`
  margin-top: 20px;
  width: 100%;
  box-sizing: border-box;
`;

const SearchResultItem = styled.div`
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 15px;
  width: 100%;
  box-sizing: border-box;
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const StudentInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  gap: 12px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`;

const ExpandButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: #6c757d;
  padding: 4px;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const RecordDeleteButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  padding: 6px 10px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 500;

  &:hover {
    background: #c82333;
  }
`;

const StudentRecordsSection = styled.div`
  margin-bottom: 15px;
  width: 100%;
  box-sizing: border-box;
`;

const RecordEditButton = styled.button`
  background: rgb(51, 66, 147);
  color: white;
  border: none;
  padding: 6px 10px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: rgb(11, 23, 92);
  }
`;

const StudentRecordsTableContainer = styled.div`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
  background: white;
`;

const StudentRecordsTable = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const StudentRecordsHeaderRow = styled.div`
  display: grid;
  grid-template-columns: 120px 130px minmax(0, 1fr) 110px;
  width: 100%;
  background: #f8f9fa;
  border-bottom: 2px solid #e9ecef;
`;

const StudentRecordsDataRow = styled.div`
  display: grid;
  grid-template-columns: 120px 130px minmax(0, 1fr) 110px;
  width: 100%;
  border-bottom: 1px solid #e9ecef;

  &:hover {
    background: #f8f9fa;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const StudentRecordsTableHeaderCell = styled.div`
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #495057;
`;

const StudentRecordsTableCell = styled.div`
  padding: 1rem;
  color: #495057;
  word-wrap: break-word;
  overflow-wrap: break-word;
  vertical-align: middle;
  min-width: 0;
`;

const EditAttendanceModalContent = styled.div`
  background: white;
  padding: 28px;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  width: 100%;
  max-width: 560px;
  box-sizing: border-box;
`;

const EditAttendanceModalTitle = styled.h2`
  margin: 0 0 8px;
  color: #2c3e50;
  font-size: 1.35rem;
`;

const EditAttendanceModalMeta = styled.p`
  margin: 0 0 20px;
  color: #6c757d;
  font-size: 0.95rem;
`;

const EditAttendanceModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
`;

const EditAttendanceModalActions = styled.div`
  display: flex;
  gap: 12px;
  margin-left: auto;
`;

const EditModalSaveButton = styled.button`
  background: rgb(51, 66, 147);
  color: white;
  border: none;
  padding: 10px 22px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const EditModalCancelButton = styled.button`
  background: #eee;
  color: #333;
  border: none;
  padding: 10px 22px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
`;

const StudentDetails = styled.div`
  flex: 1;
`;

const SearchStudentName = styled.h4`
  color: #2c3e50;
  margin: 0 0 5px 0;
  font-size: 1.1rem;
  font-weight: 600;
`;

const StudentMeta = styled.div`
  color: #6c757d;
  font-size: 0.9rem;
  
  span {
    margin-right: 15px;
  }
`;

const AttendanceEntries = styled.div`
  margin-top: 15px;
`;

const AttendanceEntry = styled.div`
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 10px;
`;

const EntryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`;

const EntryDate = styled.div`
  font-weight: 600;
  color: #2c3e50;
`;

const AddDateButton = styled.button`
  background: rgb(51, 66, 147);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgb(11, 23, 92);
    transform: translateY(-1px);
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const AttendanceForm = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  align-items: end;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const FormLabel = styled.label`
  margin-bottom: 5px;
  color: #495057;
  font-weight: 500;
  font-size: 0.9rem;
`;

const FormSelect = styled.select`
  height: 40px;
  padding: 8px 12px;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  font-size: 0.9rem;
  background: white;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);
    box-shadow: 0 0 0 2px rgba(51, 66, 147, 0.1);
  }
`;

const FormInput = styled.input`
  height: 40px;
  padding: 8px 12px;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  font-size: 0.9rem;
  background: white;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);
    box-shadow: 0 0 0 2px rgba(51, 66, 147, 0.1);
  }
`;

const TimePresetRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

const TimePresetButton = styled.button`
  padding: 6px 12px;
  border: 1px solid rgb(51, 66, 147);
  background: white;
  color: rgb(51, 66, 147);
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  line-height: 1.3;

  &:hover {
    background: rgba(51, 66, 147, 0.08);
  }
`;

const SubmitEntryButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: #218838;
    transform: translateY(-1px);
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const NoResultsText = styled.p`
  text-align: center;
  color: #6c757d;
  padding: 20px;
  font-style: italic;
`;

const DeleteButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #c82333;
    transform: translateY(-1px);
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const ClearButton = styled.button`
  color: black;
  border: 1px solid gray;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(108, 117, 125, 0.3);
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  @media (max-width: 768px) {
    padding: 15px 30px;
    font-size: 1.1rem;
  }
`;

const AttendanceTableContainer = styled.div`
  margin-top: 1rem;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
  animation: ${fadeIn} 0.3s ease-out;
  overflow-x: auto;
  width: 100%;
  
  @media (max-width: 768px) {
    margin-top: 0.8rem;
  }
`;

const AttendanceTable = styled.table`
  width: 100%;
  min-width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  background: white;
`;

const AttendanceTableHeader = styled.thead`
  background: #f8f9fa;
`;

const AttendanceTableHeaderRow = styled.tr``;

const AttendanceTableHeaderCell = styled.th`
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #e9ecef;
  min-width: 140px;
  &:last-child {
    width: 25%;
    min-width: 120px;
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
    font-size: 0.85rem;
    
    &:last-child {
      width: auto;
      min-width: unset;
    }
  }
`;

const AttendanceTableBody = styled.tbody``;

const AttendanceTableRow = styled.tr`
  border-bottom: 1px solid #e9ecef;
  
  &:hover {
    background: #f8f9fa;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const AttendanceTableCell = styled.td`
  padding: 1rem;
  color: #495057;
  word-wrap: break-word;
  overflow-wrap: break-word;
  
  &:last-child {
    min-width: 120px;
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
    font-size: 0.9rem;
    
    &:last-child {
      min-width: unset;
    }
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  text-align: center;
  min-width: 80px;
  
  &.present {
    background-color: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  }
  
  &.absent {
    background-color: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  }
  
  &.on-leave {
    background-color: #fff3cd;
    color: #856404;
    border: 1px solid #ffeaa7;
  }
  
  &.others {
    background-color: #d1ecf1;
    color: #0c5460;
    border: 1px solid #bee5eb;
  }
  
  @media (max-width: 768px) {
    padding: 4px 8px;
    font-size: 0.8rem;
    min-width: 70px;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding: 20px;
  border-top: 1px solid #e9ecef;
  background: white;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }
`;

const PaginationInfo = styled.div`
  color: #6c757d;
  font-size: 0.9rem;
  
  @media (max-width: 768px) {
    text-align: center;
  }
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  
  @media (max-width: 768px) {
    justify-content: center;
    flex-wrap: wrap;
  }
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const PageButton = styled.button`
  padding: 8px 16px;
  border: 1px solid #dee2e6;
  background: white;
  color: #495057;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #f8f9fa;
    border-color: rgb(51, 66, 147);
    color: rgb(51, 66, 147);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 0.95rem;
  }
`;

const ItemsPerPageSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  background: white;
  color: #495057;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);
    box-shadow: 0 0 0 2px rgba(51, 66, 147, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 10px 12px;
    font-size: 0.95rem;
  }
`;

const PageNumbers = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
    justify-content: center;
  }
`;

const PageNumber = styled.button`
  padding: 6px 12px;
  border: 1px solid #dee2e6;
  background: ${props => props.active ? 'rgb(51, 66, 147)' : 'white'};
  color: ${props => props.active ? 'white' : '#495057'};
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  min-width: 36px;

  &:hover:not(:disabled) {
    background: ${props => props.active ? 'rgb(41, 56, 137)' : '#f8f9fa'};
    border-color: ${props => props.active ? 'rgb(51, 66, 147)' : 'rgb(51, 66, 147)'};
    color: ${props => props.active ? 'white' : 'rgb(51, 66, 147)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AddAttendance = () => {
  const { courses: masterCourses, sessions: masterSessions } = useSelector(state => state.masterData);
  const { user } = useSelector(state => state.auth);
  const [students, setStudents] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedSession, setSelectedSession] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [attendance, setAttendance] = useState({});
  const [loading, setLoading] = useState({
    sessions: false,
    students: false,
    search: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [uploadSummary, setUploadSummary] = useState(null);
  const [successfulRecords, setSuccessfulRecords] = useState([]);

  // Student search states
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [expandedSearchStudents, setExpandedSearchStudents] = useState(new Set());
  const [studentAttendanceHistory, setStudentAttendanceHistory] = useState({});
  const [editAttendanceModal, setEditAttendanceModal] = useState(null);
  const [studentAttendanceEntries, setStudentAttendanceEntries] = useState({});
  const [allStudents, setAllStudents] = useState([]);
  const [allAttendanceRecords, setAllAttendanceRecords] = useState([]);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  // Flatten attendance records for table display and sort by attendance_date (latest first)
  const flattenedAttendanceRecords = allAttendanceRecords
    .flatMap(student => 
      (student.attendance || []).map(record => ({
        ...record,
        studentId: student.id,
        studentName: `${student.firstName} ${student.lastName}`,
        studentEmail: student.email,
        studentRegistration: student.registration_number,
        studentCourse: student.course
      }))
    )
    .sort((a, b) => new Date(b.attendance_date) - new Date(a.attendance_date)); // Sort by attendance_date descending (latest date first)

  // Use paginated records directly (already paginated from server)
  const paginatedRecords = flattenedAttendanceRecords;

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCourse, searchTerm]);

  // Use courses from Redux masterData

  // Use sessions from Redux masterData - filter by selected course
  const filteredSessions = selectedCourse 
    ? masterSessions.filter(session => session.course_id == selectedCourse || session.course_id == parseInt(selectedCourse))
    : [];

  // Fetch all students on component mount
  useEffect(() => {
    const fetchAllStudents = async () => {
      setLoading(prev => ({ ...prev, search: true }));
      try {
        const response = await axios.get(`${API_BASE_URL}/students`);
        if (response.data.success) {
          setAllStudents(response.data.students || []);
        }
      } catch (error) {
        console.error('Error fetching students:', error);
        showError('Failed to load students');
        setAllStudents([]);
      } finally {
        setLoading(prev => ({ ...prev, search: false }));
      }
    };

    fetchAllStudents();
  }, []);

  // Fetch all attendance records when filters are clear
  useEffect(() => {
    if (!selectedCourse && !searchTerm.trim()) {
      const fetchAllAttendance = async () => {
        setLoading(prev => ({ ...prev, search: true }));
        try {
          const response = await axios.get(`${API_BASE_URL}/attendance/all`, {
            params: {
              page: currentPage,
              limit: itemsPerPage
            }
          });
          if (response.data.success) {
            setAllAttendanceRecords(response.data.students || []);
            // Update pagination from API response
            if (response.data.pagination) {
              setPagination(response.data.pagination);
            }
          }
        } catch (error) {
          console.error('Error fetching all attendance:', error);
          setAllAttendanceRecords([]);
          setPagination({ page: 1, limit: itemsPerPage, total: 0, totalPages: 0 });
        } finally {
          setLoading(prev => ({ ...prev, search: false }));
        }
      };

      fetchAllAttendance();
    } else {
      setAllAttendanceRecords([]);
      setPagination({ page: 1, limit: itemsPerPage, total: 0, totalPages: 0 });
    }
  }, [selectedCourse, searchTerm, currentPage, itemsPerPage]);

  // Client-side search functionality
  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    const filteredStudents = allStudents.filter(student => {
      const searchLower = searchTerm.toLowerCase();
      return (
        student.firstName?.toLowerCase().includes(searchLower) ||
        student.lastName?.toLowerCase().includes(searchLower) ||
        student.email?.toLowerCase().includes(searchLower) ||
        student.registration_number?.toLowerCase().includes(searchLower)
      );
    });

    setSearchResults(filteredStudents);
  }, [searchTerm, allStudents]);

  const fetchStudentAttendanceHistory = async (studentId) => {
    setStudentAttendanceHistory((prev) => ({
      ...prev,
      [studentId]: { ...prev[studentId], loading: true },
    }));
    try {
      const response = await getStudentDetails(studentId);
      const records = (response.studentDetails?.attendance || [])
        .slice()
        .sort((a, b) => new Date(b.date) - new Date(a.date));
      setStudentAttendanceHistory((prev) => ({
        ...prev,
        [studentId]: { loading: false, records, fetched: true },
      }));
    } catch (error) {
      console.error('Error fetching student attendance history:', error);
      setStudentAttendanceHistory((prev) => ({
        ...prev,
        [studentId]: { loading: false, records: [], fetched: true, error: true },
      }));
    }
  };

  const toggleSearchStudentExpanded = (studentId) => {
    const isExpanded = expandedSearchStudents.has(studentId);
    setExpandedSearchStudents((prev) => {
      const next = new Set(prev);
      if (next.has(studentId)) next.delete(studentId);
      else next.add(studentId);
      return next;
    });
    if (!isExpanded && !studentAttendanceHistory[studentId]?.fetched) {
      fetchStudentAttendanceHistory(studentId);
    }
  };

  const handleDeleteAttendanceRecord = async (studentId, recordId, { closeModal = false } = {}) => {
    const ok = await showDeleteConfirm('attendance record', 'attendance record');
    if (!ok) return false;
    try {
      const res = await fetch(`${API_BASE_URL}/attendance/${recordId}`, {
        method: 'DELETE',
        headers: { ...getAuthHeaders() },
      });
      const data = await res.json();
      if (data.success) {
        showSuccess('Attendance record deleted');
        setStudentAttendanceHistory((prev) => ({
          ...prev,
          [studentId]: {
            ...prev[studentId],
            records: (prev[studentId]?.records || []).filter((r) => r.id !== recordId),
          },
        }));
        if (closeModal) setEditAttendanceModal(null);
        return true;
      }
      showError(data.message || 'Failed to delete attendance');
      return false;
    } catch (error) {
      showError('Failed to delete attendance');
      return false;
    }
  };

  const openEditAttendanceModal = (studentId, record) => {
    setEditAttendanceModal({
      studentId,
      recordId: record.id,
      date: record.date,
      course: record.course || record.course_name || 'N/A',
      status: record.status || '',
      class_joined_at: toTimeInputValue(record.class_joined_at) || DEFAULT_PRESENT_TIMES.joined,
      class_left_at: toTimeInputValue(record.class_left_at) || DEFAULT_PRESENT_TIMES.left,
      leave_reason: record.leave_reason || '',
      notes: record.notes || '',
      saving: false,
    });
  };

  const updateEditAttendanceField = (field, value) => {
    setEditAttendanceModal((prev) => {
      if (!prev) return prev;
      let next = { ...prev, [field]: value };
      if (field === 'status' && value === 'Present') {
        next = {
          ...next,
          class_joined_at: next.class_joined_at || DEFAULT_PRESENT_TIMES.joined,
          class_left_at: next.class_left_at || DEFAULT_PRESENT_TIMES.left,
        };
      }
      return next;
    });
  };

  const handleSaveEditAttendance = async () => {
    if (!editAttendanceModal) return;
    const { studentId, recordId, status, class_joined_at, class_left_at, leave_reason, notes } = editAttendanceModal;
    if (!status) {
      showError('Please select a status');
      return;
    }
    if (status === 'Present' && (!class_joined_at || !class_left_at)) {
      showError('Please provide both join and leave times for Present status');
      return;
    }
    if (status === 'On Leave' && !leave_reason.trim()) {
      showError('Please provide a reason for leave');
      return;
    }
    if (status === 'Others' && !notes.trim()) {
      showError('Please provide custom text for Others status');
      return;
    }

    setEditAttendanceModal((prev) => (prev ? { ...prev, saving: true } : prev));
    try {
      const res = await fetch(`${API_BASE_URL}/attendance/${recordId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          status,
          class_joined_at: status === 'Present' ? class_joined_at : null,
          class_left_at: status === 'Present' ? class_left_at : null,
          leave_reason: status === 'On Leave' ? leave_reason : null,
          notes: status === 'Others' ? notes : null,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        showError(data.message || 'Failed to update attendance');
        return;
      }
      showSuccess('Attendance record updated');
      setStudentAttendanceHistory((prev) => ({
        ...prev,
        [studentId]: {
          ...prev[studentId],
          records: (prev[studentId]?.records || []).map((record) =>
            record.id === recordId
              ? {
                  ...record,
                  status,
                  class_joined_at: status === 'Present' ? class_joined_at : null,
                  class_left_at: status === 'Present' ? class_left_at : null,
                  leave_reason: status === 'On Leave' ? leave_reason : null,
                  notes: status === 'Others' ? notes : null,
                }
              : record
          ),
        },
      }));
      setEditAttendanceModal(null);
    } catch (error) {
      showError('Failed to update attendance');
    } finally {
      setEditAttendanceModal((prev) => (prev ? { ...prev, saving: false } : prev));
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Present':
        return 'present';
      case 'Absent':
        return 'absent';
      case 'On Leave':
        return 'on-leave';
      case 'Others':
        return 'others';
      default:
        return '';
    }
  };

  // Add attendance entry for a student
  const addAttendanceEntry = (studentId) => {
    const entryId = `${studentId}_${Date.now()}`;
    setStudentAttendanceEntries(prev => ({
      ...prev,
      [studentId]: [
        ...(prev[studentId] || []),
        {
          id: entryId,
          date: '',
          status: '',
          class_joined_at: '',
          class_left_at: '',
          leave_reason: ''
        }
      ]
    }));
  };

  // Check if student has any attendance entries
  const hasAttendanceEntries = (studentId) => {
    return studentAttendanceEntries[studentId] && studentAttendanceEntries[studentId].length > 0;
  };

  // Update attendance entry
  const updateAttendanceEntry = (studentId, entryId, field, value) => {
    setStudentAttendanceEntries(prev => ({
      ...prev,
      [studentId]: prev[studentId].map(entry => {
        if (entry.id !== entryId) return entry;
        let next = { ...entry, [field]: value };
        if (field === 'status' && value === 'Present') {
          next = {
            ...next,
            class_joined_at: DEFAULT_PRESENT_TIMES.joined,
            class_left_at: DEFAULT_PRESENT_TIMES.left,
          };
        }
        return next;
      })
    }));
  };

  const setAttendanceEntryTimes = (studentId, entryId, joined, left) => {
    setStudentAttendanceEntries(prev => ({
      ...prev,
      [studentId]: prev[studentId].map(entry =>
        entry.id === entryId ? { ...entry, class_joined_at: joined, class_left_at: left } : entry
      )
    }));
  };

  // Remove attendance entry
  const removeAttendanceEntry = (studentId, entryId) => {
    setStudentAttendanceEntries(prev => ({
      ...prev,
      [studentId]: prev[studentId].filter(entry => entry.id !== entryId)
    }));
  };

  // Submit all attendance entries
  const submitAllAttendance = async () => {
    const allEntries = [];
    
    // Collect all valid entries from all students
    Object.keys(studentAttendanceEntries).forEach(studentId => {
      studentAttendanceEntries[studentId].forEach(entry => {
        if (entry.status && entry.date) {
          // Validate entry
          if (entry.status === 'Present' && (!entry.class_joined_at || !entry.class_left_at)) {
            showError(`Please provide both join and leave times for Present status`);
            return;
          }

          if (entry.status === 'On Leave' && !entry.leave_reason.trim()) {
            showError(`Please provide a reason for leave`);
            return;
          }

          if (entry.status === 'Others' && !entry.other_text.trim()) {
            showError(`Please provide custom text for Others status`);
            return;
          }

          allEntries.push({
            studentId: studentId,
            attendanceDate: entry.date,
            status: entry.status,
            class_joined_at: entry.status === 'Present' ? entry.class_joined_at : null,
            class_left_at: entry.status === 'Present' ? entry.class_left_at : null,
            leave_reason: entry.status === 'On Leave' ? entry.leave_reason : null,
            notes: entry.status === 'Others' ? entry.other_text : null,
            recorded_by: user?.staff_id || null
          });
        }
      });
    });

    if (allEntries.length === 0) {
      showError('No valid attendance entries to submit');
      return;
    }

    setSubmitting(true);
    try {
      // Submit all entries individually using the new individual endpoint
      const promises = allEntries.map(entry =>
        axios.post(`${API_BASE_URL}/attendance/individual`, {
          studentId: entry.studentId,
          attendanceDate: entry.attendanceDate,
          status: entry.status,
          class_joined_at: entry.class_joined_at,
          class_left_at: entry.class_left_at,
          leave_reason: entry.leave_reason,
          notes: entry.notes,
          recorded_by: entry.recorded_by,
          ...(selectedSession ? { sessionId: selectedSession } : {})
        })
      );

      await Promise.all(promises);
      showSuccess(`Attendance recorded successfully for ${allEntries.length} entries`);
      
      // Clear all entries after successful submission
      setStudentAttendanceEntries({});
    } catch (error) {
      console.error('Error submitting attendance:', error);
      showError(error.response?.data?.message || 'Failed to record attendance');
    } finally {
      setSubmitting(false);
    }
  };

  // Check if there are any valid entries to submit
  const hasValidEntries = () => {
    return Object.keys(studentAttendanceEntries).some(studentId => 
      studentAttendanceEntries[studentId].some(entry => entry.status && entry.date)
    );
  };

  // Check if all entries have required fields filled
  const areAllEntriesValid = () => {
    const allEntries = Object.values(studentAttendanceEntries).flat();
    if (allEntries.length === 0) return false;
    
    return allEntries.every(entry => {
      if (!entry.status || !entry.date) return false;
      
      if (entry.status === 'Present') {
        return entry.class_joined_at && entry.class_left_at;
      }
      
      if (entry.status === 'On Leave') {
        return entry.leave_reason && entry.leave_reason.trim() !== '';
      }
      
      if (entry.status === 'Others') {
        return entry.other_text && entry.other_text.trim() !== '';
      }
      
      return true; // Absent requires no extra fields
    });
  };

  // Clear all selections
  const handleClearSelections = () => {
    setSelectedCourse('');
    setSelectedSession('');
    setSelectedDate('');
    setStudents([]);
    setAttendance({});
    setStudentAttendanceEntries({});
    setSearchTerm('');
    setSearchResults([]);
    setAllAttendanceRecords([]);
  };

  // Clear search input
  const handleClearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
  };

  // Reset selected session when course changes
  useEffect(() => {
    setSelectedSession('');
  }, [selectedCourse]);

  useEffect(() => {
    if (selectedSession && selectedDate) {
      const fetchStudents = async () => {
        setLoading(prev => ({ ...prev, students: true }));
        try {
          // Fetch students for session only
          const studentRes = await axios.get(
            `${API_BASE_URL}/attendance/students?sessionId=${selectedSession}`
          );
          const studentData = studentRes.data.students || [];
          setStudents(studentData);
          // Initialize attendance state
          const initialAttendance = studentData.reduce((acc, student) => {
            acc[student.id] = { status: '', class_joined_at: '', class_left_at: '', leave_reason: '' };
            return acc;
          }, {});
          setAttendance(initialAttendance);
        } catch (error) {
          console.error('Error fetching students:', error);
          showError('Failed to fetch students.');
          setStudents([]);
        } finally {
          setLoading(prev => ({ ...prev, students: false }));
        }
      };
      fetchStudents();
    } else {
      setStudents([]);
      setAttendance({});
    }
  }, [selectedSession, selectedDate]);

  const handleAttendanceChange = (studentId, field, value) => {
    setAttendance(prev => {
      const current = prev[studentId] || {};
      const updated = { ...current, [field]: value };
      if (field === 'status' && value === 'Present') {
        updated.class_joined_at = DEFAULT_PRESENT_TIMES.joined;
        updated.class_left_at = DEFAULT_PRESENT_TIMES.left;
      }
      return {
        ...prev,
        [studentId]: updated,
      };
    });
  };

  const setSessionStudentTimes = (studentId, joined, left) => {
    setAttendance(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        class_joined_at: joined,
        class_left_at: left,
      },
    }));
  };

  const isStudentValid = (att) => {
    if (!att || !att.status) return false;
    if (att.status === 'Present') {
      return att.class_joined_at && att.class_left_at;
    }
    if (att.status === 'On Leave') {
      return att.leave_reason && att.leave_reason.trim() !== '';
    }
    if (att.status === 'Others') {
      return att.other_text && att.other_text.trim() !== '';
    }
    return true; // Absent requires no extra fields
  };

  const hasValidAttendanceRecords = () => {
    return Object.values(attendance).some(att => isStudentValid(att));
  };

  const isSubmitDisabled =
    !selectedCourse ||
    !selectedSession ||
    !selectedDate ||
    students.length === 0 ||
    !hasValidAttendanceRecords() ||
    submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitDisabled) return;
    
    setSubmitting(true);
    try {
      const attendanceRecords = students
        .map(student => ({
          student_id: student.id,
          status: attendance[student.id]?.status,
          class_joined_at: attendance[student.id]?.status === 'Present' ? attendance[student.id]?.class_joined_at : null,
          class_left_at: attendance[student.id]?.status === 'Present' ? attendance[student.id]?.class_left_at : null,
          leave_reason: attendance[student.id]?.status === 'On Leave' ? attendance[student.id]?.leave_reason : null,
          notes: attendance[student.id]?.status === 'Others' ? attendance[student.id]?.other_text : null,
          recorded_by: user?.staff_id || null
        }))
        .filter(record => record.status && isStudentValid(attendance[record.student_id]));
      
      await axios.post(`${API_BASE_URL}/attendance`, {
        courseId: selectedCourse,
        sessionId: selectedSession,
        attendanceDate: selectedDate,
        attendanceRecords
      });
      showSuccess('Attendance Updated');
      setSelectedCourse('');
      setSelectedSession('');
      setSelectedDate('');
      setStudents([]);
      setAttendance({});
    } catch (error) {
      showError(error.response?.data?.message || 'Error submitting attendance.');
      console.error('Error submitting attendance:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = (e) => {
    setUploadFile(e.target.files[0]);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setUploadFile(null);
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!uploadFile) return;
    
    setUploading(true);
    try {
      // Create FormData to send file
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('recorded_by', user?.staff_id || null);

    

      // Send to enhanced backend API
      const response = await axios.post(
        `${API_BASE_URL}/attendance/bulk-upload`, 
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      // Always show the result modal (success or errors)
      const errorDetails = response.data.summary?.errors || [];
      const summary = response.data.summary || {};
      const processedRecords = response.data.processedRecords || [];
      
      // Create a map of student_id + attendance_date to track which records had errors
      const errorMap = new Set();
      errorDetails.forEach(error => {
        // Extract student ID from error messages like "Row X: Student with ID Y..." or "Student Y: ..."
        const studentIdMatch = error.match(/Student (?:with ID )?(\d+)/);
        if (studentIdMatch) {
          const studentId = studentIdMatch[1];
          // Try to find the matching record in processedRecords
          processedRecords.forEach((record, idx) => {
            if (record.student_id == studentId) {
              errorMap.add(`${record.student_id}-${record.attendance_date}`);
            }
          });
        }
        // Also check for row-based errors
        const rowMatch = error.match(/Row (\d+):/);
        if (rowMatch) {
          const rowNum = parseInt(rowMatch[1]);
          const recordIndex = rowNum - 2; // Row 2 is index 0 (row 1 is header)
          if (recordIndex >= 0 && recordIndex < processedRecords.length) {
            const record = processedRecords[recordIndex];
            errorMap.add(`${record.student_id}-${record.attendance_date}`);
          }
        }
      });
      
      // Filter successful records (those not in errorMap)
      const successful = processedRecords.filter(record => {
        const key = `${record.student_id}-${record.attendance_date}`;
        return !errorMap.has(key);
      });
      
      setUploadErrors(errorDetails);
      setUploadSummary(summary);
      setSuccessfulRecords(successful);
      setErrorModalOpen(true);
      
      // Log all errors to console for debugging
      if (errorDetails.length > 0) {
        console.error('Upload errors:', errorDetails);
      }
    } catch (err) {
      console.error('Upload error:', err);
      const errorDetails = err.response?.data?.summary?.errors || [];
      const summary = err.response?.data?.summary || {
        totalRecords: 0,
        totalErrors: errorDetails.length,
        savedCount: 0,
        updatedCount: 0
      };
      const processedRecords = err.response?.data?.processedRecords || [];
      
      // Create error map
      const errorMap = new Set();
      errorDetails.forEach(error => {
        const studentIdMatch = error.match(/Student (?:with ID )?(\d+)/);
        if (studentIdMatch) {
          const studentId = studentIdMatch[1];
          processedRecords.forEach((record) => {
            if (record.student_id == studentId) {
              errorMap.add(`${record.student_id}-${record.attendance_date}`);
            }
          });
        }
        const rowMatch = error.match(/Row (\d+):/);
        if (rowMatch) {
          const rowNum = parseInt(rowMatch[1]);
          const recordIndex = rowNum - 2;
          if (recordIndex >= 0 && recordIndex < processedRecords.length) {
            const record = processedRecords[recordIndex];
            errorMap.add(`${record.student_id}-${record.attendance_date}`);
          }
        }
      });
      
      const successful = processedRecords.filter(record => {
        const key = `${record.student_id}-${record.attendance_date}`;
        return !errorMap.has(key);
      });
      
      setUploadErrors(errorDetails);
      setUploadSummary(summary);
      setSuccessfulRecords(successful);
      setErrorModalOpen(true);
      
      if (errorDetails.length > 0) {
        console.error('Detailed errors:', errorDetails);
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <AddAttendanceContainer>
      <Title style={{ marginBottom: 0 }}>Add Attendance</Title>
      
      <TopRow>
        <TopRowItem>
          <SelectGroup>
            <SelectIcon><FaBook /></SelectIcon>
            <CourseSelect
              value={selectedCourse}
              onChange={(value) => setSelectedCourse(value)}
              placeholder="Select a Course"
              isClearable={false}
              as={UniformSelect}
            />
          </SelectGroup>
        </TopRowItem>
        <TopRowItem>
          <SelectGroup>
            <SelectIcon><FaRegCalendarAlt /></SelectIcon>
            <SessionSelect
              value={selectedSession}
              onChange={(value) => setSelectedSession(value)}
              placeholder={filteredSessions.length > 0 ? 'Select a Session' : 'No sessions available'}
              isClearable={false}
              isDisabled={!selectedCourse || filteredSessions.length === 0}
              courseId={selectedCourse}
              sessions={masterSessions}
              as={UniformSelect}
            />
          </SelectGroup>
        </TopRowItem>
        <TopRowItem>
          <SelectGroup>
            <SelectIcon><FaCalendarAlt /></SelectIcon>
            <UniformInput
              type="date"
              value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
              required
              disabled={!selectedSession}
            />
          </SelectGroup>
        </TopRowItem>
        <UniformButton
          onClick={() => setModalOpen(true)}
          style={{ float: 'none', alignSelf: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}
        >
          <FaUpload style={{ marginRight: 6 }} /> Upload Attendance File
        </UniformButton>
      </TopRow>
      
      {/* Clear Button - only show when course is selected */}
      {selectedCourse && (
        <div style={{ marginBottom : '20px', display: 'flex',  width: '190px' }}>
          <ClearButton onClick={handleClearSelections}>
            <FaTimes style={{ marginRight: '6px' }} />
            Clear 
          </ClearButton>
        </div>
      )}
      
      {/* Student Search Section - only show when no course is selected */}
      {!selectedCourse && (
        <SearchSection>
          <SearchTitle>Search Student</SearchTitle>
          <SearchInputContainer>
            <SearchIcon><FaSearch /></SearchIcon>
            <SearchInput
              type="text"
              placeholder="Search by student name, email, or registration number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <ClearSearchIcon onClick={handleClearSearch}>
                <FaTimes />
              </ClearSearchIcon>
            )}
          </SearchInputContainer>
        
        {loading.search && (
          <NoResultsText>Loading students...</NoResultsText>
        )}
        
        {!loading.search && !searchTerm.trim() && flattenedAttendanceRecords.length > 0 && (
          <>
            <AttendanceTableContainer>
              <AttendanceTable>
                <AttendanceTableHeader>
                  <AttendanceTableHeaderRow>
                    <AttendanceTableHeaderCell style={{ width: '25%' }}>Student Name</AttendanceTableHeaderCell>
                    <AttendanceTableHeaderCell style={{ width: '25%' }}>Email</AttendanceTableHeaderCell>
                    <AttendanceTableHeaderCell style={{ width: '25%' }}>Session</AttendanceTableHeaderCell>
                    <AttendanceTableHeaderCell style={{ width: '25%' }}>Date</AttendanceTableHeaderCell>
                    <AttendanceTableHeaderCell style={{ width: '25%' }}>Status</AttendanceTableHeaderCell>
                    <AttendanceTableHeaderCell style={{ width: '25%' }}>Time</AttendanceTableHeaderCell>
                    <AttendanceTableHeaderCell style={{ width: '25%' }}>Leave Reason</AttendanceTableHeaderCell>
                    <AttendanceTableHeaderCell style={{ width: '25%' }}>Notes</AttendanceTableHeaderCell>
                  </AttendanceTableHeaderRow>
                </AttendanceTableHeader>
                <AttendanceTableBody>
                  {paginatedRecords.map((record, index) => (
                    <AttendanceTableRow key={`${record.studentId}-${record.id}-${index}`}>
                      <AttendanceTableCell>{record.studentName}</AttendanceTableCell>
                      <AttendanceTableCell>{record.studentEmail || 'N/A'}</AttendanceTableCell>
                      <AttendanceTableCell>{record.studentRegistration || 'N/A'}</AttendanceTableCell>
                      <AttendanceTableCell>
                        {new Date(record.attendance_date).toLocaleDateString()}
                      </AttendanceTableCell>
                      <AttendanceTableCell>
                        <StatusBadge 
                          className={
                            record.status === 'Present' ? 'present' :
                            record.status === 'Absent' ? 'absent' :
                            record.status === 'On Leave' ? 'on-leave' :
                            record.status === 'Others' ? 'others' : ''
                          }
                        >
                          {record.status}
                        </StatusBadge>
                      </AttendanceTableCell>
                      <AttendanceTableCell>
                        {record.status === 'Present' && record.class_joined_at ? record.class_joined_at : '-'} - {record.status === 'Present' && record.class_left_at ? record.class_left_at : ''}
                      </AttendanceTableCell>
                      <AttendanceTableCell>{record.status === 'On Leave' && record.leave_reason ? record.leave_reason : ''}</AttendanceTableCell>
                      <AttendanceTableCell>{record.status === 'Others' && record.notes ? record.notes : ''}</AttendanceTableCell>
                    </AttendanceTableRow>
                  ))}
                </AttendanceTableBody>
              </AttendanceTable>
            </AttendanceTableContainer>
            
            {pagination.totalPages > 1 && (
              <PaginationContainer>
                <PaginationInfo>
                  Showing {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} records
                </PaginationInfo>
                <PaginationControls>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <label style={{ fontSize: '0.9rem', color: '#6c757d' }}>Items per page:</label>
                    <ItemsPerPageSelect
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </ItemsPerPageSelect>
                  </div>
                  <PaginationButtons>
                    <PageButton
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={pagination.page === 1 || loading.search}
                    >
                      Previous
                    </PageButton>
                    <PageNumbers>
                      {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                        let pageNum;
                        if (pagination.totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (pagination.page <= 3) {
                          pageNum = i + 1;
                        } else if (pagination.page >= pagination.totalPages - 2) {
                          pageNum = pagination.totalPages - 4 + i;
                        } else {
                          pageNum = pagination.page - 2 + i;
                        }
                        return (
                          <PageNumber
                            key={pageNum}
                            active={pageNum === pagination.page}
                            onClick={() => setCurrentPage(pageNum)}
                          >
                            {pageNum}
                          </PageNumber>
                        );
                      })}
                    </PageNumbers>
                    <PageButton
                      onClick={() => setCurrentPage(prev => Math.min(pagination.totalPages, prev + 1))}
                      disabled={pagination.page >= pagination.totalPages || loading.search}
                    >
                      Next
                    </PageButton>
                  </PaginationButtons>
                </PaginationControls>
              </PaginationContainer>
            )}
          </>
        )}
        
        {!loading.search && searchResults.length > 0 && (
          <SearchResultsContainer>
            {searchResults.map(student => {
              const isExpanded = expandedSearchStudents.has(student.id);
              const history = studentAttendanceHistory[student.id];
              return (
              <SearchResultItem key={student.id}>
                <StudentInfo>
                  <ExpandButton
                    type="button"
                    onClick={() => toggleSearchStudentExpanded(student.id)}
                    aria-expanded={isExpanded}
                    aria-label={isExpanded ? 'Collapse attendance records' : 'Expand attendance records'}
                  >
                    {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                  </ExpandButton>
                  <StudentDetails>
                    <SearchStudentName>{student.firstName} {student.lastName}</SearchStudentName>
                    <StudentMeta>
                      <span>Email: {student.email}</span>
                      <span>Registration: {student.registration_number || 'N/A'}</span>
                      <span>Course: {student.course || 'N/A'}</span>
                    </StudentMeta>
                  </StudentDetails>
                  {!hasAttendanceEntries(student.id) && (
                    <AddDateButton onClick={() => addAttendanceEntry(student.id)}>
                      <FaPlus /> Add Attendance
                    </AddDateButton>
                  )}
                </StudentInfo>

                {isExpanded && (
                  <StudentRecordsSection>
                    {history?.loading ? (
                      <NoResultsText style={{ marginTop: 0 }}>Loading attendance records...</NoResultsText>
                    ) : history?.error ? (
                      <NoResultsText style={{ marginTop: 0 }}>Failed to load attendance records.</NoResultsText>
                    ) : history?.records?.length > 0 ? (
                      <StudentRecordsTableContainer>
                        <StudentRecordsTable>
                          <StudentRecordsHeaderRow>
                            <StudentRecordsTableHeaderCell>Date</StudentRecordsTableHeaderCell>
                            <StudentRecordsTableHeaderCell>Status</StudentRecordsTableHeaderCell>
                            <StudentRecordsTableHeaderCell>Course</StudentRecordsTableHeaderCell>
                            <StudentRecordsTableHeaderCell>Actions</StudentRecordsTableHeaderCell>
                          </StudentRecordsHeaderRow>
                          {history.records.map((record, idx) => (
                            <StudentRecordsDataRow key={record.id || idx}>
                              <StudentRecordsTableCell>
                                {record.date ? new Date(record.date).toLocaleDateString() : 'N/A'}
                              </StudentRecordsTableCell>
                              <StudentRecordsTableCell>
                                <StatusBadge className={getStatusBadgeClass(record.status)}>
                                  {record.status || 'N/A'}
                                </StatusBadge>
                              </StudentRecordsTableCell>
                              <StudentRecordsTableCell>{record.course || record.course_name || 'N/A'}</StudentRecordsTableCell>
                              <StudentRecordsTableCell>
                                {record.id ? (
                                  <RecordEditButton
                                    type="button"
                                    onClick={() => openEditAttendanceModal(student.id, record)}
                                  >
                                    <FaPencilAlt /> Edit
                                  </RecordEditButton>
                                ) : null}
                              </StudentRecordsTableCell>
                            </StudentRecordsDataRow>
                          ))}
                        </StudentRecordsTable>
                      </StudentRecordsTableContainer>
                    ) : (
                      <NoResultsText style={{ marginTop: 0 }}>No attendance records found.</NoResultsText>
                    )}
                  </StudentRecordsSection>
                )}
                
                {studentAttendanceEntries[student.id] && studentAttendanceEntries[student.id].length > 0 && (
                  <AttendanceEntries>
                    {studentAttendanceEntries[student.id].map(entry => (
                      <AttendanceEntry key={entry.id}>
                        <EntryHeader>
                          <EntryDate>
                            {entry.date ? new Date(entry.date).toLocaleDateString() : 'Select Date'}
                          </EntryDate>
                          <DeleteButton onClick={() => removeAttendanceEntry(student.id, entry.id)}>
                            <FaTrash /> Delete
                          </DeleteButton>
                        </EntryHeader>
                        
                        <AttendanceForm>
                          <FormGroup>
                            <FormLabel>Date</FormLabel>
                            <FormInput
                              type="date"
                              value={entry.date}
                              onChange={(e) => updateAttendanceEntry(student.id, entry.id, 'date', e.target.value)}
                            />
                          </FormGroup>
                          
                          <FormGroup>
                            <FormLabel>Status</FormLabel>
                            <FormSelect
                              value={entry.status}
                              onChange={(e) => updateAttendanceEntry(student.id, entry.id, 'status', e.target.value)}
                            >
                              <option value="">Select Status</option>
                              <option value="Present">Present</option>
                              <option value="Absent">Absent</option>
                              <option value="On Leave">On Leave</option>
                              <option value="Others">Others</option>
                            </FormSelect>
                          </FormGroup>
                          
                          {entry.status === 'Present' && (
                            <>
                              <FormGroup>
                                <FormLabel>Class Joined At</FormLabel>
                                <FormInput
                                  type="time"
                                  value={entry.class_joined_at}
                                  onChange={(e) => updateAttendanceEntry(student.id, entry.id, 'class_joined_at', e.target.value)}
                                />
                              </FormGroup>
                              
                              <FormGroup>
                                <FormLabel>Class Left At</FormLabel>
                                <FormInput
                                  type="time"
                                  value={entry.class_left_at}
                                  onChange={(e) => updateAttendanceEntry(student.id, entry.id, 'class_left_at', e.target.value)}
                                />
                              </FormGroup>

                              <FormGroup style={{ gridColumn: '1 / -1' }}>
                                <FormLabel>Time presets</FormLabel>
                                <TimePresetRow>
                                  {TIME_PRESETS.map(p => (
                                    <TimePresetButton
                                      key={p.key}
                                      type="button"
                                      onClick={() => setAttendanceEntryTimes(student.id, entry.id, p.joined, p.left)}
                                    >
                                      {p.label}
                                    </TimePresetButton>
                                  ))}
                                </TimePresetRow>
                              </FormGroup>
                            </>
                          )}
                          
                          {entry.status === 'On Leave' && (
                            <FormGroup style={{ gridColumn: '1 / -1' }}>
                              <FormLabel>Leave Reason</FormLabel>
                              <FormInput
                                type="text"
                                placeholder="Enter reason for leave"
                                value={entry.leave_reason}
                                onChange={(e) => updateAttendanceEntry(student.id, entry.id, 'leave_reason', e.target.value)}
                              />
                            </FormGroup>
                          )}
                          
                          {entry.status === 'Others' && (
                            <FormGroup style={{ gridColumn: '1 / -1' }}>
                              <FormLabel>Custom Text</FormLabel>
                              <FormInput
                                type="text"
                                placeholder="Enter custom text"
                                value={entry.other_text || ''}
                                onChange={(e) => updateAttendanceEntry(student.id, entry.id, 'other_text', e.target.value)}
                              />
                            </FormGroup>
                          )}
                          
                          <FormGroup>
                            <AddDateButton onClick={() => addAttendanceEntry(student.id)} style={{ width: "170px" }}>
                              <FaPlus /> Add Another Date
                            </AddDateButton>
                          </FormGroup>
                        </AttendanceForm>
                      </AttendanceEntry>
                    ))}
                  </AttendanceEntries>
                )}
              </SearchResultItem>
            );
            })}
          </SearchResultsContainer>
        )}
        
        {!loading.search && searchTerm && searchResults.length === 0 && (
          <NoResultsText>No students found matching your search.</NoResultsText>
        )}
        
        {!loading.search && !searchTerm.trim() && allAttendanceRecords.length === 0 && (
          <NoResultsText>No attendance records found.</NoResultsText>
        )}
        
        {/* Global Submit Button */}
        {hasValidEntries() && (
          <div style={{ marginTop: '20px' }}>
            <SubmitButton 
              type="button" 
              onClick={submitAllAttendance}
              disabled={submitting || !areAllEntriesValid()}
              style={{ 
                background: 'rgb(51, 66, 147)', 
                color: 'white', 
                padding: '12px 30px', 
                fontSize: '1rem',
                fontWeight: '600',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {submitting ? 'Submitting...' : 'Submit Attendance'}
            </SubmitButton>
          </div>
        )}
        </SearchSection>
      )}
      <FormContainer>
        {masterCourses.length > 0 ? (
          <Form onSubmit={handleSubmit}>
            {loading.students && <LoadingText>Loading Students...</LoadingText>}
            {!loading.students && selectedCourse && students.length === 0 && (
              <InfoText>No students enrolled in this course or session for the selected date.</InfoText>
            )}
            {!loading.students && students.length > 0 && selectedSession && selectedDate && (
              <StudentListContainer>
                <StudentListHeader>
                  <span>Student Name</span>
                  <span>Registration No.</span>
                  <span>Email</span>
                  <span></span>
                  <span>Attendance Status</span>
                </StudentListHeader>
                {students.map(student => (
                  <StudentItem key={student.id}>
                    <StudentName>{student.first_name} {student.last_name}</StudentName>
                    <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>{student.registration_number || 'N/A'}</span>
                    <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>{student.email || 'N/A'}</span>
                    <span></span>
                    
                    {/* Mobile-specific student information */}
                    <MobileStudentInfo>
                      <div className="student-detail">
                        <span className="student-detail-label">Registration:</span>
                        <span>{student.registration_number || 'N/A'}</span>
                      </div>
                      <div className="student-detail">
                        <span className="student-detail-label">Email:</span>
                        <span>{student.email || 'N/A'}</span>
                      </div>
                    </MobileStudentInfo>
                    
                    <MobileAttendanceSection>
                      <Select
                        value={attendance[student.id]?.status || ''}
                        onChange={e => handleAttendanceChange(student.id, 'status', e.target.value)}
                        className="attendance-select"
                      >
                        <option value="">Select Status</option>
                        <option value="Present">Present</option>
                        <option value="Absent">Absent</option>
                        <option value="On Leave">On Leave</option>
                        <option value="Others">Others</option>
                      </Select>
                      {attendance[student.id]?.status === 'Present' && (
                        <>
                          <div className="time-inputs">
                            <TimeInput
                              type="time"
                              value={attendance[student.id]?.class_joined_at || ''}
                              onChange={e => handleAttendanceChange(student.id, 'class_joined_at', e.target.value)}
                              placeholder="Class Joined At"
                              required
                            />
                            <TimeInput
                              type="time"
                              value={attendance[student.id]?.class_left_at || ''}
                              onChange={e => handleAttendanceChange(student.id, 'class_left_at', e.target.value)}
                              placeholder="Class Finished/Leaved At"
                              required
                            />
                          </div>
                          <div className="time-presets">
                            {TIME_PRESETS.map(p => (
                              <TimePresetButton
                                key={p.key}
                                type="button"
                                onClick={() => setSessionStudentTimes(student.id, p.joined, p.left)}
                              >
                                {p.label}
                              </TimePresetButton>
                            ))}
                          </div>
                        </>
                      )}
                      {attendance[student.id]?.status === 'On Leave' && (
                        <div>
                          <LeaveReasonInput
                            type="text"
                            value={attendance[student.id]?.leave_reason || ''}
                            onChange={e => handleAttendanceChange(student.id, 'leave_reason', e.target.value)}
                            placeholder="Reason for Leave"
                            required
                          />
                        </div>
                      )}
                      {attendance[student.id]?.status === 'Others' && (
                        <div>
                          <LeaveReasonInput
                            type="text"
                            value={attendance[student.id]?.other_text || ''}
                            onChange={e => handleAttendanceChange(student.id, 'other_text', e.target.value)}
                            placeholder="Enter custom text"
                            required
                          />
                        </div>
                      )}
                    </MobileAttendanceSection>
                  </StudentItem>
                ))}
              </StudentListContainer>
            )}
            {students.length > 0 && (
              <SubmitButton type="submit" disabled={isSubmitDisabled}>
                {submitting ? 'Submitting...' : 'Submit Attendance'}
              </SubmitButton>
            )}
          </Form>
        ) : (
          <InfoText>No courses found. Please add a course first.</InfoText>
        )}
      </FormContainer>

      <Modal
        isOpen={Boolean(editAttendanceModal)}
        onRequestClose={() => setEditAttendanceModal(null)}
        contentLabel="Edit Attendance Record"
        ariaHideApp={false}
        style={{
          overlay: {
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.3)',
          },
          content: {
            position: 'static',
            margin: 'auto',
            inset: 'unset',
            border: 'none',
            background: 'none',
            padding: 0,
            maxWidth: 560,
            width: '100%',
          },
        }}
      >
        {editAttendanceModal && (
          <EditAttendanceModalContent>
            <EditAttendanceModalTitle>Edit Attendance</EditAttendanceModalTitle>
            <EditAttendanceModalMeta>
              {editAttendanceModal.date
                ? new Date(editAttendanceModal.date).toLocaleDateString()
                : 'N/A'}
              {' · '}
              {editAttendanceModal.course}
            </EditAttendanceModalMeta>

            <AttendanceForm style={{ gridTemplateColumns: '1fr' }}>
              <FormGroup>
                <FormLabel>Status</FormLabel>
                <FormSelect
                  value={editAttendanceModal.status}
                  onChange={(e) => updateEditAttendanceField('status', e.target.value)}
                >
                  <option value="">Select Status</option>
                  <option value="Present">Present</option>
                  <option value="Absent">Absent</option>
                  <option value="On Leave">On Leave</option>
                  <option value="Others">Others</option>
                </FormSelect>
              </FormGroup>

              {editAttendanceModal.status === 'Present' && (
                <>
                  <FormGroup>
                    <FormLabel>Class Joined At</FormLabel>
                    <FormInput
                      type="time"
                      value={editAttendanceModal.class_joined_at}
                      onChange={(e) => updateEditAttendanceField('class_joined_at', e.target.value)}
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>Class Left At</FormLabel>
                    <FormInput
                      type="time"
                      value={editAttendanceModal.class_left_at}
                      onChange={(e) => updateEditAttendanceField('class_left_at', e.target.value)}
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>Time presets</FormLabel>
                    <TimePresetRow>
                      {TIME_PRESETS.map((p) => (
                        <TimePresetButton
                          key={p.key}
                          type="button"
                          onClick={() =>
                            setEditAttendanceModal((prev) =>
                              prev
                                ? { ...prev, class_joined_at: p.joined, class_left_at: p.left }
                                : prev
                            )
                          }
                        >
                          {p.label}
                        </TimePresetButton>
                      ))}
                    </TimePresetRow>
                  </FormGroup>
                </>
              )}

              {editAttendanceModal.status === 'On Leave' && (
                <FormGroup>
                  <FormLabel>Leave Reason</FormLabel>
                  <FormInput
                    type="text"
                    placeholder="Enter reason for leave"
                    value={editAttendanceModal.leave_reason}
                    onChange={(e) => updateEditAttendanceField('leave_reason', e.target.value)}
                  />
                </FormGroup>
              )}

              {editAttendanceModal.status === 'Others' && (
                <FormGroup>
                  <FormLabel>Custom Text</FormLabel>
                  <FormInput
                    type="text"
                    placeholder="Enter custom text"
                    value={editAttendanceModal.notes}
                    onChange={(e) => updateEditAttendanceField('notes', e.target.value)}
                  />
                </FormGroup>
              )}
            </AttendanceForm>

            <EditAttendanceModalFooter>
              <RecordDeleteButton
                type="button"
                onClick={() =>
                  handleDeleteAttendanceRecord(editAttendanceModal.studentId, editAttendanceModal.recordId, {
                    closeModal: true,
                  })
                }
              >
                Delete
              </RecordDeleteButton>
              <EditAttendanceModalActions>
                <EditModalCancelButton type="button" onClick={() => setEditAttendanceModal(null)}>
                  Cancel
                </EditModalCancelButton>
                <EditModalSaveButton
                  type="button"
                  onClick={handleSaveEditAttendance}
                  disabled={editAttendanceModal.saving}
                >
                  {editAttendanceModal.saving ? 'Saving...' : 'Save'}
                </EditModalSaveButton>
              </EditAttendanceModalActions>
            </EditAttendanceModalFooter>
          </EditAttendanceModalContent>
        )}
      </Modal>

      <Modal
        isOpen={modalOpen}
        onRequestClose={handleModalClose}
        contentLabel="Upload Attendance File"
        ariaHideApp={false}
        style={{
          overlay: {
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.3)'
          },
          content: {
            position: 'static',
            margin: 'auto',
            inset: 'unset',
            border: 'none',
            background: 'none',
            padding: 0,
            maxWidth: 400,
            width: '100%'
          }
        }}
      >
        <ModalContent>
          <h2>Upload Attendance File</h2>
          <form onSubmit={handleUploadSubmit} style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <input
              type="file"
              accept=".csv, .xls, .xlsx"
              onChange={handleFileChange}
              disabled={uploading}
              style={{ marginBottom: 20 }}
            />
            <ModalButtonRow>
              <button type="submit" disabled={!uploadFile || uploading} style={{ padding: '10px 22px', borderRadius: 8, background: '#3f51b5', color: 'white', border: 'none', fontWeight: 600, fontSize: '1rem' }}>
                {uploading ? 'Uploading...' : 'Submit'}
              </button>
              <button type="button" onClick={handleModalClose} style={{ padding: '10px 22px', borderRadius: 8, background: '#eee', color: '#333', border: 'none', fontWeight: 500, fontSize: '1rem' }}>
                Cancel
              </button>
            </ModalButtonRow>
          </form>
        </ModalContent>
      </Modal>

      {/* Upload Results Modal */}
      <Modal
        isOpen={errorModalOpen}
        onRequestClose={() => setErrorModalOpen(false)}
        contentLabel={uploadErrors.length > 0 ? "Upload Errors" : "Upload Results"}
        ariaHideApp={false}
        style={{
          overlay: {
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.5)'
          },
          content: {
            position: 'static',
            margin: 'auto',
            inset: 'unset',
            border: 'none',
            background: 'none',
            padding: 0,
            maxWidth: 600,
            width: '90vw'
          }
        }}
      >
        <ErrorModalContent>
          <ErrorModalHeader>
            {uploadErrors.length > 0 ? (
              <>
                <ErrorModalTitle>Upload Results</ErrorModalTitle>
              </>
            ) : (
              <>
                <SuccessIcon>✓</SuccessIcon>
                <ErrorModalTitle style={{ color: '#28a745' }}>Upload Successful</ErrorModalTitle>
              </>
            )}
          </ErrorModalHeader>
          
          {uploadSummary && (
            <>
              {uploadSummary.savedCount > 0 || uploadSummary.updatedCount > 0 ? (
                <SuccessSummary>
                  {uploadSummary.savedCount > 0 && `${uploadSummary.savedCount} record(s) saved. `}
                  {uploadSummary.updatedCount > 0 && `${uploadSummary.updatedCount} record(s) updated. `}
                  {uploadSummary.totalErrors > 0 && `${uploadSummary.totalErrors} error(s) encountered.`}
                  {uploadSummary.totalErrors === 0 && 'All records processed successfully!'}
                </SuccessSummary>
              ) : (
                <ErrorSummary>
                  Failed to process attendance records. {uploadSummary.totalErrors} error(s) encountered.
                </ErrorSummary>
              )}
            </>
          )}

          {successfulRecords.length > 0 && (
            <>
              <h3 style={{ margin: '0 0 15px 0', color: '#495057', fontSize: '1.1rem' }}>
                Successfully Uploaded Records:
              </h3>
              <SuccessList>
                {successfulRecords.map((record, index) => (
                  <SuccessItem key={index}>
                    <strong>Student ID {record.student_id}</strong> - Date: {record.attendance_date} - Status:{' '}
                    <StatusBadge 
                      className={
                        record.status === 'Present' ? 'present' :
                        record.status === 'Absent' ? 'absent' :
                        record.status === 'On Leave' ? 'on-leave' :
                        record.status === 'Others' ? 'others' : ''
                      }
                      style={{ marginLeft: '8px' }}
                    >
                      {record.status}
                    </StatusBadge>
                    {record.class_joined_at && ` - Time: ${record.class_joined_at}${record.class_left_at ? ` to ${record.class_left_at}` : ''}`}
                  </SuccessItem>
                ))}
              </SuccessList>
            </>
          )}

          {uploadErrors.length > 0 && (
            <>
              <h3 style={{ margin: '0 0 15px 0', color: '#495057', fontSize: '1.1rem' }}>
                Problem Records:
              </h3>
              <ErrorList>
                {uploadErrors.map((error, index) => (
                  <ErrorItem key={index}>{error}</ErrorItem>
                ))}
              </ErrorList>
            </>
          )}

          <ErrorModalButtons>
            <ErrorModalButton
              className="secondary"
              onClick={() => {
                setErrorModalOpen(false);
                handleModalClose(); // Close upload modal too
              }}
            >
              Done
            </ErrorModalButton>
            {uploadErrors.length > 0 && (
              <ErrorModalButton
                className="primary"
                onClick={() => {
                  setErrorModalOpen(false);
                  // Keep upload modal open for re-upload
                }}
              >
                Re-upload
              </ErrorModalButton>
            )}
          </ErrorModalButtons>
        </ErrorModalContent>
      </Modal>
    </AddAttendanceContainer>
  );
};

export default AddAttendance;